import { useState, type FormEvent } from 'react';
import { KeyRound, Search, ShieldCheck, UserCog, Users } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  ROLE_LABEL,
  USER_STATUSES,
  useRoles,
  useSetStaffRole,
  useSetStaffStatus,
  useStaff,
  useStaffMember,
  type UserRole,
  type UserStatus,
} from '@/lib/users-api';
import { cn, errorMessage, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * Staff accounts and roles (spec §29, §58).
 *
 * Creating an account is deliberately absent: it needs an invitation with a
 * set-password link, which needs the notification transport that does not exist
 * yet. An admin form that types somebody else's password would be worse than
 * the gap.
 *
 * The permission list on the drawer shows what the guard will **actually**
 * allow, overrides applied — not what the role nominally grants. Deny wins on a
 * per-user override, so the two can differ, and the one that matters is the one
 * the guard uses.
 */

type Panel = 'role' | 'status';

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const staff = useStaff({
    page,
    limit: 20,
    search,
    ...(role ? { role } : {}),
  });
  const roles = useRoles();

  const totalStaff = staff.data?.pagination.total ?? 0;
  const suspended = staff.data?.items.filter((user) => user.status !== 'ACTIVE').length ?? 0;

  return (
    <div>
      <PageHeader
        title="Users & Roles"
        description="Who works here, and what each of them is allowed to do."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {staff.isLoading ? (
          [0, 1, 2].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Staff accounts"
              value={formatNumber(totalStaff)}
              icon={<Users className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Roles in use"
              value={formatNumber(roles.data?.filter((entry) => entry.users > 0).length ?? 0)}
              icon={<ShieldCheck className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Not active on this page"
              value={formatNumber(suspended)}
              icon={<UserCog className="size-5" aria-hidden="true" />}
              tone={suspended > 0 ? 'warning' : 'neutral'}
            />
          </>
        )}
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search name or email"
              className="pl-9"
              aria-label="Search staff"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Role">
            {([['', 'All roles'], ...Object.entries(ROLE_LABEL)] as Array<[string, string]>).map(
              ([value, label]) => (
                <button
                  key={value || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={role === value}
                  onClick={() => {
                    setRole(value as UserRole | '');
                    setPage(1);
                  }}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                    role === value
                      ? 'bg-brand-600 text-white'
                      : 'text-ink-muted hover:bg-canvas hover:text-ink',
                  )}
                >
                  {label}
                </button>
              ),
            )}
          </div>
        </div>
      </Card>

      <Card>
        {staff.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : staff.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load staff">{errorMessage(staff.error)}</ErrorAlert>
          </div>
        ) : (staff.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nobody matches" description="Try a different role or search." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Last signed in</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {staff.data?.items.map((user) => (
                <tr key={user.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{user.name}</p>
                    <p className="text-xs text-ink-subtle">{user.email}</p>
                  </Td>
                  <Td>
                    <Badge tone="brand">{ROLE_LABEL[user.role]}</Badge>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(user.status)}>{user.status.toLowerCase()}</Badge>
                  </Td>
                  <Td className="text-xs text-ink-subtle">
                    {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
                  </Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(user.id)}>
                      Manage
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {staff.data && staff.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {staff.data.pagination.total} staff · page {staff.data.pagination.page} of{' '}
              {staff.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!staff.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!staff.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="mt-3 text-xs text-ink-subtle">
        Adding a colleague needs an emailed invitation, which arrives with the notification
        transport. Until then, staff accounts are created by the seed.
      </p>

      <StaffDrawer userId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function StaffDrawer({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const detail = useStaffMember(userId);
  const [panel, setPanel] = useState<Panel | null>(null);

  const user = detail.data;

  // Grouped so a 36-line permission list reads as a handful of areas.
  const grouped = (user?.effectivePermissions ?? []).reduce<Record<string, string[]>>(
    (accumulator, code) => {
      const [group = 'other'] = code.split('.');
      (accumulator[group] ??= []).push(code);
      return accumulator;
    },
    {},
  );

  return (
    <Drawer
      open={Boolean(userId)}
      onClose={() => {
        setPanel(null);
        onClose();
      }}
      title={user?.name ?? 'Staff member'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this account">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        user && (
          <div className="space-y-5">
            <section>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone="brand">{ROLE_LABEL[user.role]}</Badge>
                <Badge tone={statusTone(user.status)}>{user.status.toLowerCase()}</Badge>
              </div>
              <p className="text-sm text-ink">{user.email}</p>
              {user.mobile && <p className="text-xs text-ink-subtle">{user.mobile}</p>}
              <p className="mt-1 text-xs text-ink-subtle">
                {user.lastLoginAt
                  ? `Last signed in ${formatDateTime(user.lastLoginAt)}`
                  : 'Has never signed in'}
                {user.lastLoginIp && ` from ${user.lastLoginIp}`}
              </p>
            </section>

            {panel ? (
              <StaffActionForm
                panel={panel}
                user={user}
                onDone={() => setPanel(null)}
              />
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => setPanel('role')}>
                    <KeyRound className="size-4" aria-hidden="true" />
                    Change role
                  </Button>
                  <Button variant="outline" onClick={() => setPanel('status')}>
                    <UserCog className="size-4" aria-hidden="true" />
                    Change status
                  </Button>
                </div>
                <p className="mt-2 text-xs text-ink-subtle">
                  You cannot change your own role or status — ask another administrator.
                </p>
              </section>
            )}

            <section>
              <h3 className="mb-1 text-sm font-semibold text-ink">
                Permissions ({user.effectivePermissions.length})
              </h3>
              <p className="mb-3 text-xs text-ink-subtle">
                What the guard will actually allow, per-user overrides applied.
              </p>

              <div className="space-y-2">
                {Object.entries(grouped).map(([group, codes]) => (
                  <div key={group} className="rounded-lg border border-line p-3">
                    <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-subtle">
                      {group}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {codes.map((code) => (
                        <Badge key={code} tone="neutral">
                          {code.split('.')[1]}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {user.overrides.length > 0 && (
                <div className="mt-3 rounded-lg bg-canvas p-3">
                  <p className="text-xs font-medium text-ink">Per-user overrides</p>
                  {user.overrides.map((override) => (
                    <p key={override.code} className="mt-1 text-xs text-ink-muted">
                      {override.granted ? 'Granted' : 'Denied'} · {override.code}
                    </p>
                  ))}
                </div>
              )}
            </section>
          </div>
        )
      )}
    </Drawer>
  );
}

function StaffActionForm({
  panel,
  user,
  onDone,
}: {
  panel: Panel;
  user: { id: string; role: UserRole; status: UserStatus };
  onDone: () => void;
}) {
  const setRole = useSetStaffRole();
  const setStatus = useSetStaffStatus();

  const mutation = panel === 'role' ? setRole : setStatus;

  const [role, setRoleValue] = useState<UserRole>(user.role);
  const [status, setStatusValue] = useState<UserStatus>(user.status);
  const [reason, setReason] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const onSuccess = () => onDone();

    if (panel === 'role') {
      setRole.mutate({ userId: user.id, role }, { onSuccess });
    } else {
      setStatus.mutate(
        { userId: user.id, status, reason: reason || undefined },
        { onSuccess },
      );
    }
  };

  return (
    <section className="rounded-lg border border-line p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">
        {panel === 'role' ? 'Change role' : 'Change status'}
      </h3>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {mutation.isError && (
          <ErrorAlert title="Could not do that">{errorMessage(mutation.error)}</ErrorAlert>
        )}

        {panel === 'role' ? (
          <Field
            label="Role"
            htmlFor="role"
            required
            hint="Takes effect immediately — their cached permissions are cleared."
          >
            <Select
              id="role"
              value={role}
              onChange={(event) => setRoleValue(event.target.value as UserRole)}
            >
              {(Object.keys(ROLE_LABEL) as UserRole[]).map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <>
            <Field label="Status" htmlFor="status" required>
              <Select
                id="status"
                value={status}
                onChange={(event) => setStatusValue(event.target.value as UserStatus)}
              >
                {USER_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, ' ').toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Reason" htmlFor="reason" hint="Recorded on the audit entry.">
              <Textarea
                id="reason"
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
          </>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Confirm'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
